import React, { useState, useEffect } from 'react';
import { Activity, Users as UsersIcon, Bell, AlertTriangle, UserPlus } from 'lucide-react';
import { useSupabaseClient } from '../utils/supabase';

function Dashboard() {
  const [stats, setStats] = useState({
    activeUsers: 0,
    totalEndpoints: 0,
    alertTypes: 0,
    totalWebhooks: 0,
    adminUsers: 0,
    regularUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [makingAdmin, setMakingAdmin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminResult, setAdminResult] = useState<{success?: boolean; message?: string} | null>(null);

  const supabase = useSupabaseClient();
  
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      // Fetch each statistic individually to handle errors more gracefully
      const usersResponse = await supabase.from('users').select('*', { count: 'exact' });
      const alertTypesResponse = await supabase.from('alert_types').select('*', { count: 'exact' });
      const webhooksResponse = await supabase.from('webhooks').select('*', { count: 'exact' });
      
      // Try to get role data if the column exists
      const roleResponse = await supabase.from('users').select('role');
      
      // Process user counts
      const usersCount = usersResponse.count || 0;
      const alertTypesCount = alertTypesResponse.count || 0;
      const webhooksCount = webhooksResponse.count || 0;
      
      // Calculate admin/regular user counts
      let adminUsers = 0;
      let regularUsers = 0;
      
      if (roleResponse.data && roleResponse.data.length > 0) {
        // Count each role in the results
        roleResponse.data.forEach(user => {
          if (user.role === 'admin') {
            adminUsers++;
          } else {
            regularUsers++;
          }
        });
      }

      setStats({
        activeUsers: usersCount,
        totalEndpoints: usersCount, // Using users as a proxy for endpoints
        alertTypes: alertTypesCount,
        totalWebhooks: webhooksCount,
        adminUsers,
        regularUsers
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Set default stats as fallback
      setStats({
        activeUsers: 0,
        totalEndpoints: 0,
        alertTypes: 0,
        totalWebhooks: 0,
        adminUsers: 0,
        regularUsers: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const assignAdminRole = async () => {
    if (!adminEmail.trim()) return;
    
    setMakingAdmin(true);
    setAdminResult(null);
    
    try {
      const { data, error } = await supabase.rpc('assign_admin_role', { 
        user_email: adminEmail.trim() 
      });
      
      if (error) {
        setAdminResult({ success: false, message: error.message });
      } else {
        setAdminResult(data);
        if (data?.success) {
          setAdminEmail('');
          // Refresh stats to show updated admin count
          fetchStats();
        }
      }
    } catch (error) {
      console.error('Error assigning admin role:', error);
      setAdminResult({ 
        success: false, 
        message: error instanceof Error ? error.message : 'Unknown error occurred' 
      });
    } finally {
      setMakingAdmin(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900">Admin Dashboard</h1>
      
      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {/* Stats cards */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UsersIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Users
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {loading ? '...' : stats.activeUsers}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <span className="font-medium text-green-700 mr-2">
                {loading ? '...' : stats.adminUsers} admins
              </span>
              <span className="font-medium text-blue-700">
                {loading ? '...' : stats.regularUsers} regular users
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Bell className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Connected Endpoints
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {loading ? '...' : stats.totalEndpoints}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Alert Types
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {loading ? '...' : stats.alertTypes}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Activity className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Notifications
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {loading ? '...' : stats.totalWebhooks}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Admin role assignment section */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            User Management
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Assign admin roles to users
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="max-w-lg">
            <label htmlFor="admin-email" className="block text-sm font-medium text-gray-700">
              Assign Admin Role By Email
            </label>
            <div className="mt-1 flex rounded-md shadow-sm">
              <div className="relative flex items-stretch flex-grow">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserPlus className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="admin-email"
                  id="admin-email"
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-none rounded-l-md pl-10 sm:text-sm border-gray-300"
                  placeholder="user@example.com"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                />
              </div>
              <button
                type="button"
                onClick={assignAdminRole}
                disabled={makingAdmin || !adminEmail.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
              >
                {makingAdmin ? 'Processing...' : 'Make Admin'}
              </button>
            </div>
            
            {adminResult && (
              <div className={`mt-3 p-3 rounded ${adminResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                {adminResult.message}
              </div>
            )}
            
            <p className="mt-4 text-sm text-gray-500">
              Assign admin role to an existing user by their email address. Admins have full access to the dashboard and can manage all users and notifications.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;